import { useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ChevronLeft,
  Filter,
  RefreshCw,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Alert, Button, Card, CardContent } from "@/components/ui";
import { cn } from "@/lib/utils";

type DeferredFilterActionsProps = {
  hasUnappliedChanges: boolean;
  hasActiveFilters: boolean;
  onApply: () => void;
  onReset: () => void;
  applyLabel: string;
  applyLabelUnchanged?: string;
  resetLabel: string;
  unsavedHint?: string;
  className?: string;
  resetVariant?: "outline" | "ghost";
  applyPrimary?: boolean;
  applyDisabled?: boolean;
  align?: "split" | "end";
};

export function DeferredFilterActions({
  hasUnappliedChanges,
  hasActiveFilters,
  onApply,
  onReset,
  applyLabel,
  applyLabelUnchanged,
  resetLabel,
  unsavedHint,
  className,
  resetVariant = "outline",
  applyPrimary = false,
  applyDisabled = false,
  align = "split",
}: DeferredFilterActionsProps) {
  return (
    <>
      <div
        className={cn(
          align === "end"
            ? "flex items-center justify-end gap-2 border-t border-surface-200 pt-2"
            : "flex flex-col-reverse gap-3 border-t border-surface-200 pt-4 sm:flex-row sm:items-center sm:justify-between",
          className,
        )}
      >
        <Button
          variant={resetVariant}
          size="sm"
          onClick={onReset}
          disabled={!hasActiveFilters}
          leftIcon={<X className="h-4 w-4" />}
          className={
            resetVariant === "ghost"
              ? "text-surface-600 hover:text-error-600"
              : undefined
          }
        >
          {resetLabel}
        </Button>
        <Button
          variant={applyPrimary ? "primary" : "outline"}
          size={applyPrimary ? undefined : "sm"}
          onClick={onApply}
          disabled={applyDisabled}
          leftIcon={<Search className="h-4 w-4" />}
          className={cn(
            applyPrimary && "w-full sm:w-auto",
            hasUnappliedChanges && "ring-2 ring-primary-300 ring-offset-2",
          )}
        >
          {hasUnappliedChanges || !applyLabelUnchanged
            ? applyLabel
            : applyLabelUnchanged}
        </Button>
      </div>
      {unsavedHint && hasUnappliedChanges ? (
        <Alert
          variant="warning"
          icon={RefreshCw}
          className="mt-4 justify-center rounded-lg px-3 py-2"
        >
          {unsavedHint}
        </Alert>
      ) : null}
    </>
  );
}

type DeferredFilterSectionProps = {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
  className?: string;
  compact?: boolean;
};

export function DeferredFilterSection({
  icon: Icon,
  title,
  children,
  className,
  compact = false,
}: DeferredFilterSectionProps) {
  return (
    <div className={cn(compact ? "mb-4" : "mb-6", className)}>
      <div className={cn("flex items-center gap-2", compact ? "mb-2" : "mb-3")}>
        <Icon className="h-4 w-4 text-primary-500" />
        {compact ? (
          <span className="text-sm font-medium text-surface-700">{title}</span>
        ) : (
          <h4 className="text-xs font-semibold text-surface-600">
            {title}
          </h4>
        )}
      </div>
      {children}
    </div>
  );
}

type DeferredFilterPanelBaseProps = {
  children: ReactNode;
  hasUnappliedChanges: boolean;
  onApply: () => void;
  onReset: () => void;
  applyLabel: string;
  applyLabelUnchanged?: string;
  resetLabel: string;
  unsavedHint?: string;
  applyDisabled?: boolean;
  className?: string;
};

type CardDeferredFilterPanelProps = DeferredFilterPanelBaseProps & {
  variant: "card";
  title: string;
  hint: string;
  activeHint: (count: number) => string;
  activeFiltersCount: number;
  showLabel: string;
  hideLabel: string;
};

type CompactDeferredFilterPanelProps = DeferredFilterPanelBaseProps & {
  variant: "compact";
  panelTitle: string;
  activeFiltersCount: number;
  clearAllLabel: string;
};

export type DeferredFilterPanelProps =
  | CardDeferredFilterPanelProps
  | CompactDeferredFilterPanelProps;

export function DeferredFilterPanel(props: DeferredFilterPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const hasActiveFilters = props.activeFiltersCount > 0;

  if (props.variant === "compact") {

    return (
      <div className={cn("space-y-4", props.className)}>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setExpanded((current) => !current)}
            className="flex items-center gap-2 text-sm font-medium text-surface-700 hover:text-surface-900"
          >
            <Filter className="h-4 w-4" />
            <span>{props.panelTitle}</span>
            {hasActiveFilters ? (
              <span className="rounded-full bg-primary-fill px-2 py-0.5 text-xs text-white">
                {props.activeFiltersCount}
              </span>
            ) : null}
          </button>
          {hasActiveFilters ? (
            <Button variant="outline" size="sm" onClick={props.onReset}>
              <X className="mr-2 h-4 w-4" />
              {props.clearAllLabel}
            </Button>
          ) : null}
        </div>
        {expanded ? (
          <div className="space-y-4 rounded-xl border border-surface-200 bg-surface-50 p-4">
            {props.children}
            <DeferredFilterActions
              hasUnappliedChanges={props.hasUnappliedChanges}
              hasActiveFilters={hasActiveFilters}
              onApply={props.onApply}
              onReset={props.onReset}
              applyLabel={props.applyLabel}
              resetLabel={props.resetLabel}
              applyDisabled={!props.hasUnappliedChanges}
              align="end"
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Card className={cn("overflow-hidden border-2 border-surface-200", props.className)}>
      <div
        className="flex cursor-pointer items-center justify-between bg-gradient-to-r from-surface-50 to-surface-100 px-5 py-4"
        onClick={() => setExpanded((current) => !current)}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
            <SlidersHorizontal className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-surface-900">{props.title}</h3>
            <p className="text-sm text-surface-500">
              {props.activeFiltersCount > 0 ? (
                <span className="flex items-center gap-1.5">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-fill text-xs font-medium text-white">
                    {props.activeFiltersCount}
                  </span>
                  {props.activeHint(props.activeFiltersCount)}
                </span>
              ) : (
                props.hint
              )}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="gap-2">
          {expanded ? (
            <>
              <ChevronLeft className="h-4 w-4 rotate-90" />
              {props.hideLabel}
            </>
          ) : (
            <>
              <ChevronLeft className="h-4 w-4 -rotate-90" />
              {props.showLabel}
            </>
          )}
        </Button>
      </div>
      {expanded ? (
        <CardContent className="border-t border-surface-200 bg-surface-0 p-5">
          {props.children}
          <DeferredFilterActions
            hasUnappliedChanges={props.hasUnappliedChanges}
            hasActiveFilters={hasActiveFilters}
            onApply={props.onApply}
            onReset={props.onReset}
            applyLabel={props.applyLabel}
            applyLabelUnchanged={props.applyLabelUnchanged}
            resetLabel={props.resetLabel}
            unsavedHint={props.unsavedHint}
            resetVariant="ghost"
            applyPrimary
            className="border-surface-100 pt-5"
          />
        </CardContent>
      ) : null}
    </Card>
  );
}
